import { createSlug, subscribeToBusinessType } from '@/services/firebase/firestore';
import { EmptyState } from '@components/EmptyState';
import {
  DatabaseCheckIcon,
  EmptyStateIcon,
  IconArrowForward,
  IconGenStars,
  IconSearch,
  MagicSearchIcon,
} from '@components/icons';
import { Input } from '@components/Input';
import { ModalLayout } from '@components/ModalLayout';
import { TypeAhead } from '@components/TypeAhead';
import { useMemo, useState } from 'react';
import styles from './AddNewTypeModal.module.scss';

// Loading spinner
const LoadingSpinner = ({ size = 60 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 60 60"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.loadingSpinner}
  >
    <circle
      cx="30"
      cy="30"
      r="25"
      fill="none"
      stroke="url(#gradient)"
      strokeWidth="4"
      strokeLinecap="round"
      strokeDasharray="120 40"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 30 30"
        to="360 30 30"
        dur="1s"
        repeatCount="indefinite"
      />
    </circle>
    <defs>
      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#6366F1" />
      </linearGradient>
    </defs>
  </svg>
);

type ModalState = 'initial' | 'loading' | 'results' | 'exists' | 'not-found';

interface ObjectiveOrPainpoint {
  id: string;
  text: string;
}

export interface MockMetadata {
  website_objectives: string[];
  industry_painpoints: string[];
}

export interface AddNewTypeModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /** Save handler - handles lookup and creation */
  onSave?: (typeName: string, onExist: () => void, onCreated: () => void) => void;

  /** Metadata update handler - called when user saves edited metadata */
  onMetadataUpdate?: (typeName: string, metadata: { website_objectives: string[]; industry_painpoints: string[] }) => void;

  /** Discard handler - deletes the slug */
  onDiscard?: (typeName: string) => void;

  /** Open existing type handler */
  onOpenExisting?: (typeName: string) => void;

  /** Available business types from JSON */
  availableTypes: string[];

  /** Existing business types to show validation */
  existingTypes: string[];

  /** Mock data for demo mode - bypasses Firebase subscription */
  mockData?: MockMetadata;

  /** Delay in ms before showing mock results (default: 2000) */
  mockDelay?: number;
}

export const AddNewTypeModal = ({
  isOpen,
  onClose,
  onSave,
  onMetadataUpdate,
  onDiscard,
  onOpenExisting,
  availableTypes,
  existingTypes,
  mockData,
  mockDelay = 2000,
}: AddNewTypeModalProps) => {
  const [modalState, setModalState] = useState<ModalState>('initial');
  const [inputValue, setInputValue] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [typeName, setTypeName] = useState('');
  const [objectives, setObjectives] = useState<ObjectiveOrPainpoint[]>([]);
  const [painpoints, setPainpoints] = useState<ObjectiveOrPainpoint[]>([]);

  // Convert availableTypes to MenuSelectItem format for TypeAhead
  const typeAheadItems = useMemo(() => {
    return availableTypes.map((type) => ({
      value: type,
      label: type,
    }));
  }, [availableTypes]);

  if (!isOpen) return null;

  const handleLookup = () => {
    if (!inputValue.trim()) return;

    // Use the input value directly (allow any business type, not just curated list)
    const businessTypeName = inputValue.trim();

    // Check if business type already exists in the local list
    const alreadyExists = existingTypes.some(
      (existingName) => existingName.toLowerCase() === businessTypeName.toLowerCase()
    );

    if (alreadyExists) {
      setTypeName(businessTypeName);
      setModalState('exists');
      return;
    }

    setTypeName(businessTypeName);
    setModalState('loading');

    // Call the save handler with callbacks
    if (onSave) {
      onSave(
        businessTypeName,
        // onExist callback - business type already exists
        () => {
          setModalState('exists');
        },
        // onCreated callback - new business type created, listen for AI generation
        () => {
          // If mockData is provided, use it instead of Firebase subscription (demo mode)
          if (mockData) {
            setTimeout(() => {
              const { website_objectives = [], industry_painpoints = [] } = mockData;
              setObjectives(
                website_objectives.map((text: string, index: number) => ({
                  id: (index + 1).toString(),
                  text,
                }))
              );
              setPainpoints(
                industry_painpoints.map((text: string, index: number) => ({
                  id: (index + 1).toString(),
                  text,
                }))
              );
              setModalState('results');
            }, mockDelay);
            return;
          }

          // Create slug from the business type name using the shared createSlug function
          const slug = createSlug(businessTypeName);

          // Listen to Firestore for AI-generated content using the correct slug
          const unsubscribe = subscribeToBusinessType(slug, (businessType) => {
            if (businessType) {
              // Check if we have metadata - even if arrays are empty, show results
              const { metadata } = businessType;

              if (metadata) {
                const { website_objectives = [], industry_painpoints = [] } = metadata;

                // Only proceed if we have meaningful AI-generated content (more than 1 item in either array)
                if (website_objectives.length > 1 || industry_painpoints.length > 1) {
                  // Set the objectives and painpoints
                  setObjectives(
                    website_objectives.map((text: string, index: number) => ({
                      id: (index + 1).toString(),
                      text,
                    }))
                  );
                  setPainpoints(
                    industry_painpoints.map((text: string, index: number) => ({
                      id: (index + 1).toString(),
                      text,
                    }))
                  );

                  setModalState('results');
                  unsubscribe(); // Stop listening once we get meaningful data
                }
              }
            }
          });
        }
      );
    }
  };

  const handleSave = () => {
    // If we have results and the metadata was edited, update Firebase
    if (modalState === 'results' && onMetadataUpdate && typeName) {
      const metadata = {
        website_objectives: objectives.map(obj => obj.text).filter(text => text.trim() !== ''),
        industry_painpoints: painpoints.map(pp => pp.text).filter(text => text.trim() !== ''),
      };
      onMetadataUpdate(typeName, metadata);
    }
    handleClose();
  };

  const handleDiscard = () => {
    if (modalState === 'results' || modalState === 'exists') {
      // Delete the created slug
      if (onDiscard && typeName) {
        onDiscard(typeName);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setModalState('initial');
    setInputValue('');
    setSelectedType(null);
    setTypeName('');
    setObjectives([]);
    setPainpoints([]);
    onClose();
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleClose}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers to prevent backdrop click propagation */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ModalLayout
          title="Add New Type"
          onDismiss={handleClose}
          showFooter={modalState === 'results'}
          onPrimaryAction={handleSave}
          onSecondaryAction={handleDiscard}
          width={800}
        >
          <div className={styles.modal}>
            {/* Search with TypeAhead (always visible) */}
            <div className={styles.search}>
              <div className={styles.typeAheadWrapper}>
                <TypeAhead
                  items={typeAheadItems}
                  value={selectedType}
                  onChange={(value) => {
                    setSelectedType(value);
                    if (value) {
                      setInputValue(value);
                    } else {
                      setInputValue('');
                      setModalState('initial');
                      setTypeName('');
                      setObjectives([]);
                      setPainpoints([]);
                    }
                  }}
                  onInputChange={(value) => {
                    setInputValue(value);
                    if (!value) {
                      // Field was cleared - restore to initial state
                      setModalState('initial');
                      setTypeName('');
                      setObjectives([]);
                      setPainpoints([]);
                    } else if (modalState === 'not-found') {
                      // User is typing again after a failed search - restore to initial
                      setModalState('initial');
                    }
                  }}
                  placeholder="Enter business type"
                  disabled={modalState !== 'initial' && modalState !== 'not-found'}
                  clearable
                />
              </div>
              <button
                className={styles.lookupBtn}
                onClick={handleLookup}
                disabled={
                  (modalState !== 'initial' && modalState !== 'not-found') || !inputValue.trim()
                }
              >
                <IconSearch size={20} />
                <span>Lookup</span>
              </button>
            </div>

            {/* Initial State: Empty state */}
            {modalState === 'initial' && (
              <div className={styles.empty}>
                <MagicSearchIcon width={80} height={80} />
                <p className={styles.emptyText}>Enter a business type above to get started</p>
              </div>
            )}

            {/* Not Found State: Business type not in available list */}
            {modalState === 'not-found' && (
              <div className={styles.empty}>
                <EmptyState
                  icon={<EmptyStateIcon width={82} height={62} />}
                  title="Business Type Not Found"
                  subtitle={`"${inputValue}" is not in our curated business types list. Please try a different term or check the spelling.`}
                />
              </div>
            )}

            {/* Loading State */}
            {modalState === 'loading' && (
              <div className={styles.loading}>
                <LoadingSpinner size={60} />
                <p className={styles.loadingText}>Analyzing {typeName}...</p>
              </div>
            )}

            {/* Results State */}
            {modalState === 'results' && (
              <div className={styles.results}>
                <div className={styles.resultsHeader}>
                  <h3 className={styles.resultsTitle}>{typeName}</h3>
                  <p className={styles.resultsSubtitle}>
                    We created a draft with AI suggestions. Review and edit before saving.
                  </p>
                </div>

                {/* Website Objectives */}
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <IconGenStars size={18} />
                    <h4 className={styles.sectionTitle}>Website Objectives</h4>
                  </div>
                  <div className={styles.items}>
                    {objectives.map((objective) => (
                      <Input
                        key={objective.id}
                        value={objective.text}
                        onChange={(value) => {
                          setObjectives((prev) =>
                            prev.map((obj) =>
                              obj.id === objective.id ? { ...obj, text: value } : obj
                            )
                          );
                        }}
                        clearable
                        className={styles.editableInput}
                      />
                    ))}
                  </div>
                </div>

                {/* Industry Painpoints */}
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <IconGenStars size={18} />
                    <h4 className={styles.sectionTitle}>Industry Painpoints</h4>
                  </div>
                  <div className={styles.items}>
                    {painpoints.map((painpoint) => (
                      <Input
                        key={painpoint.id}
                        value={painpoint.text}
                        onChange={(value) => {
                          setPainpoints((prev) =>
                            prev.map((pp) =>
                              pp.id === painpoint.id ? { ...pp, text: value } : pp
                            )
                          );
                        }}
                        clearable
                        className={styles.editableInput}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Exists State - Business type already exists */}
            {modalState === 'exists' && (
              <div className={styles.empty}>
                <EmptyState
                  icon={<DatabaseCheckIcon width={82} height={82} />}
                  subtitle="Good News - this business type has already been setup"
                  primaryActionText="Manage Business Type"
                  hidePrimaryActionLeadingIcon
                  primaryActionTrailingIcon={<IconArrowForward size={18} />}
                  onPrimaryAction={() => {
                    if (onOpenExisting) {
                      onOpenExisting(typeName);
                    }
                    handleClose();
                  }}
                  showSecondaryAction
                  secondaryActionText="Search for a different type"
                  onSecondaryAction={() => {
                    setModalState('initial');
                    setInputValue('');
                    setTypeName('');
                  }}
                />
              </div>
            )}
          </div>
        </ModalLayout>
      </div>
    </div>
  );
};
